import React, { useEffect, useState } from 'react'
import {
  Container,
  NoMessage,
  SelectedProductsWrapper,
  Div,
  TopRow,
  NoteContainer,
  NoteIcon,
  NoteText
} from './SelectedModels.styles'
import SelectedModel from '../SelectedModel/SelectedModel'
import { Product } from '../../services/types/product'
import { LoadBar } from '../LoadCapacityPercentageBar/LoadCapacityPercentageBar'
import Checkbox from '../CheckBox/Checkbox'
import Button from '../Button/Button'

type SelectedModelsProps = {
  selectedProducts: Product[]
  setSelectedProducts: React.Dispatch<React.SetStateAction<Product[]>>
  jobSiteCapacity: number
}

const SelectedModels: React.FC<SelectedModelsProps> = ({
  selectedProducts,
  setSelectedProducts,
  jobSiteCapacity
}) => {
  const [activeProduct, setActiveProduct] = useState('')
  const [percentage, setPercentage] = useState(0)
  const [jobSiteChecked, setJobSiteChecked] = useState<boolean>(false)
  const handleSelectionChange = (productId: string) => {
    setActiveProduct(productId)
  }

  const handleClearSelection = () => {
    setSelectedProducts([])
    setJobSiteChecked(false)
  }

  useEffect(() => {
    const calculatePercentage = () => {
      console.log('=== CALCULATING PERCENTAGE ===')
      console.log('Selected Products:', selectedProducts)
      console.log('JobSite Checked:', jobSiteChecked)
      console.log('JobSite Capacity:', jobSiteCapacity)

      const total = selectedProducts.reduce((acc, product) => {
        // Log all product details for debugging
        console.log(`Product ${product.id}:`, {
          partName: product.partName,
          productNumber: product.productNumber,
          pointValue: product.pointValue,
          units: product.units,
          FTLQuantity: product.FTLQuantity
        })

        // Skip if any required value is missing or invalid
        const units = product.units ?? 0
        const ftlQuantity = product.FTLQuantity ?? 0
        const pointValue = product.pointValue ?? 0

        if (ftlQuantity === 0) {
          console.log(`  -> Skipped: FTLQuantity is 0 (division by zero)`)
          return acc
        }

        if (pointValue === 0) {
          console.log(`  -> Skipped: pointValue is 0`)
          return acc
        }

        if (units === 0) {
          console.log(`  -> Skipped: units is 0`)
          return acc
        }

        // Calculate contribution: (100 / FTLQuantity) * units
        const contribution = (100 / ftlQuantity) * units
        console.log(`  -> Contribution: (100 / ${ftlQuantity}) * ${units} = ${contribution}`)

        return acc + contribution
      }, 0)

      console.log('Total before jobsite:', total)

      const jobsiteAdjustment = jobSiteChecked ? Number(jobSiteCapacity) : 0
      const finalPercentage = Math.round(total + jobsiteAdjustment)

      console.log('Jobsite adjustment:', jobsiteAdjustment)
      console.log('Final percentage:', finalPercentage)
      console.log('=== END CALCULATION ===')

      setPercentage(finalPercentage)
    }
    calculatePercentage()
  }, [selectedProducts, jobSiteChecked, jobSiteCapacity])

  return (
    <Container>
      <LoadBar percentage={percentage} />
      <Div>
        <TopRow>
          <Checkbox
            label="Jobsite order"
            checked={jobSiteChecked}
            setChecked={setJobSiteChecked}
            info="Truckload capacity will be adjusted to allow room for unloading"
          />
          <NoteContainer>
            <NoteIcon>⚠</NoteIcon>
            <NoteText>
              <span className="note-label">Important Note:</span>
              <span className="note-content">
                Fill Percentage is approximate. Each truckload configuration is
                unique and may require minor adjustments once planning is
                complete.
              </span>
            </NoteText>
          </NoteContainer>
          <Button text={'Clear All'} onClick={handleClearSelection} />
        </TopRow>
      </Div>

      <SelectedProductsWrapper>
        {selectedProducts.length === 0 ? (
          <NoMessage>
            <p>No products selected</p>
            <span>
              Please select products from the list to begin the load simulation.
            </span>
          </NoMessage>
        ) : (
          <>
            {selectedProducts.map((product) => (
              <SelectedModel
                key={product.id}
                product={product}
                active={activeProduct === product.id}
                onSelectionChange={handleSelectionChange}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
              />
            ))}
          </>
        )}
      </SelectedProductsWrapper>
    </Container>
  )
}

export default SelectedModels
